'use client';

import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useMemo } from 'react';
import { clearAuthCookies } from '@/features/auth/utils/authCookiesManager';
import {
  ProfileIcon,
  HeartIcon,
  SecurityIcon,
  LogoutIcon,
} from '@/context/global/components/g00/g00-d00-g009-cw-account-sidebar';

export type AccountMenuId = 'edit-profile' | 'favorites' | 'terms' | 'policy' | 'logout';

export interface AccountMenuItem {
  id: AccountMenuId;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  isActive: boolean;
}

interface UseAccountMenuItemsProps {
  currentPage: AccountMenuId;
  onLogoutClick?: () => void;
}

export const useAccountMenuItems = ({
  currentPage,
  onLogoutClick,
}: UseAccountMenuItemsProps): AccountMenuItem[] => {
  const t = useTranslations();
  const router = useRouter();

  const menuItems = useMemo<AccountMenuItem[]>(
    () => [
      {
        id: 'edit-profile',
        label: t('AccountSidebar.editProfile'),
        icon: <ProfileIcon />,
        onClick: () => router.push('/edit-profile'),
        isActive: currentPage === 'edit-profile',
      },
      {
        id: 'favorites',
        label: t('AccountSidebar.favoriteSongs'),
        icon: <HeartIcon />,
        onClick: () => router.push('/favorites'),
        isActive: currentPage === 'favorites',
      },
      {
        id: 'terms',
        label: t('AccountSidebar.terms'),
        icon: <SecurityIcon />,
        onClick: () => router.push('/term'),
        isActive: currentPage === 'terms',
      },
      {
        id: 'policy',
        label: t('AccountSidebar.policy'),
        icon: <SecurityIcon />,
        onClick: () => router.push('/policy'),
        isActive: currentPage === 'policy',
      },
      {
        id: 'logout',
        label: t('AccountSidebar.logout'),
        icon: <LogoutIcon />,
        onClick: () => {
          if (onLogoutClick) {
            onLogoutClick();
          } else {
            clearAuthCookies();
            window.location.href = '/';
          }
        },
        isActive: false,
      },
    ],
    [currentPage, router, t, onLogoutClick],
  );

  return menuItems;
};
